/**
 * MypageHomeContainer
 * --
 */
import React from 'react';
import MypageHomePresenter from './MypageHomePresenter';
import { useMyPoint, useMyShopOrderList } from 'hooks/custom';
import {
  useDefaultShopDataStack,
  useUserDataStack,
  useUserStack,
  useUserAction,
  useUserDataAction,
} from 'store';
import Formatter from 'utils/Formatter';
import {
  extractApplicationPolicy,
  STORE_URL,
  getCookie,
  deleteCookie,
} from 'utils';
import dayjs from 'dayjs';
import { useGetGrades } from 'hooks/GradeHooks';
import { useGetMemberShipGrades } from 'hooks/MemberShipGradeHooks';
import { useGetStatisticsSummary } from 'hooks/StatisticsHooks';
import { useAuthCheck } from 'hooks/AuthHooks';

const MypageHomeContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== Props ===== */
  const { navigate, screenSize } = props;

  /* ===== STATE ===== */
  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userAction = useUserAction();
  const userDataAction = useUserDataAction();
  const { authCheck } = useAuthCheck();

  const { products, policies, promotion, settings } = useDefaultShopDataStack();
  const { reviews, couponList, wishlist, inquiryList } = useUserDataStack();

  // 합산 기준일
  const GradeStandard = React.useMemo(() => {
    if (!settings) return;

    const { grade_setting } = settings;

    const standardDay = dayjs()
      .subtract(grade_setting.total_month_standard, 'month')
      .set('date', grade_setting.date_of_change)
      .startOf('day')
      .format('YYYY-MM-DD');

    return standardDay;
  }, [settings]);

  /* ===== Data ===== */
  // 등급 데이터
  const { data: Grades } = useGetGrades(
    {
      is_enable: 'Y',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return null;
        else return data;
      },
      initialData: [],
    },
  );
  // 등급 내역
  const { data: GradeHistories } = useGetMemberShipGrades(
    {
      user_id: userInfo.user_id,
    },
    {
      enabled: !!userInfo,
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];
        else return data;
      },
      initialData: [],
    },
  );
  // 구매 건수, 구매 금액
  const { data: Summary } = useGetStatisticsSummary(
    'user-total-purchase',
    {
      userId: userInfo.user_id,
      startDate: GradeStandard,
      endDate: dayjs().format('YYYY-MM-DD'),
    },
    {
      enabled: !!(userInfo && GradeStandard),
      select: (res) => {
        const { status, data } = res;

        if (status !== 200)
          return {
            total_count: 0,
            total_price: 0,
          };
        else return data;
      },
      initialData: {
        total_count: 0,
        total_price: 0,
      },
    },
  );

  /* ===== Hooks ===== */
  const orderList = useMyShopOrderList(userInfo.user_id);
  const { useable } = useMyPoint(userInfo.user_id);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /**
   * 최근 주문 내역 정제
   * --
   */
  const RecentOrderList = React.useMemo(() => {
    // 렌탈 상품 필터
    const filterOrderList = orderList.filter(
      (order) => order.type !== 'RENTAL',
    );

    const newOrderList = filterOrderList
      .sort((a, b) => b.created_at - a.created_at)
      .slice(0, 5);

    return newOrderList;
  }, [orderList]);

  /**
   * 회원 요약 데이터
   * --
   */
  const UserInfoData = React.useMemo(() => {
    // NOTE: 사용 가능 쿠폰
    const useAbleCoupon = couponList.filter(
      (coupon) =>
        !coupon.use_date && coupon.expired_date > Formatter.dateToUnix(),
    );

    // 역순 정렬
    const sortHistory = [...GradeHistories].sort(
      (a, b) => b.created_at - a.created_at,
    );
    // 같은 등급 처음 달성일 추출
    const fid = sortHistory.findIndex(
      (history) => history.grade_id !== userInfo.grade_id,
    );
    const lastIndex = fid === -1 ? sortHistory.length - 1 : fid;

    return {
      ...userInfo,
      reviews: reviews.length,
      coupons: useAbleCoupon.length,
      point: useable,
      grade: Grades?.find((grade) => grade.grade_id === userInfo.grade_id),
      grade_start_date: sortHistory?.[lastIndex]?.created_at,
      grade_end_date: dayjs().unix(),
    };
  }, [userInfo, reviews, couponList, useable, GradeHistories, Grades]);

  /**
   * 주문/배송 현황
   * --
   */
  const DeliveryInfo = React.useMemo(() => {
    if (!orderList.length) return;

    // 배송, 정기배송 분류
    const divisionOrder = orderList.reduce((acc, order) => {
      if (!order.type || !order.parcel_service.length) return acc;

      if (order.type === 'DELIVERY') {
        const deliveryList = order.parcel_service.map((parcel) => ({
          delivery_status: parcel.delivery_status,
          payment_state: order.payment_state,
        }));
        acc = acc.concat(deliveryList);
      } else if (order.type === 'SUBSCRIPTION') {
        const regularList = order?.parcel_service.reduce((regular, cur) => {
          const newRegularList = cur.regular_payment_info.map((rg) => ({
            delivery_status: rg?.delivery_status,
            payment_state: rg?.payment_state,
          }));

          regular = regular.concat(newRegularList);

          return regular;
        }, []);
        acc = acc.concat(regularList);
      } else if (order.type === 'STORE_PICKUP') {
        const pickupList = order.parcel_service.map((parcel) => ({
          delivery_status: parcel.delivery_status,
          payment_state: order.payment_state,
        }));
        acc = acc.concat(pickupList);
      }

      return acc;
    }, []);

    let deposit = 0, // 입금대기
      pending = 0, // 상품 준비중
      shipping = 0, // 배송중
      delivered = 0; // 배송완료
    divisionOrder.forEach((_item) => {
      if (_item.delivery_status === 'DELIVERED') {
        return ++delivered;
      } else if (
        _item.delivery_status === 'EXCHANGE_COMPLETED' ||
        _item.delivery_status === 'RETURN_REQUESTED' ||
        _item.delivery_status === 'ON_HOLD' ||
        _item.delivery_status === 'RETURN_COMPLETED'
      ) {
        return;
      } else if (_item.delivery_status === 'SHIPPING') {
        return ++shipping;
      } else if (_item.payment_state === 'COMPLETED') {
        return ++pending;
      } else if (_item.payment_state === 'PENDING') {
        return ++deposit;
      } else {
        return;
      }
    });

    return [
      {
        title: '입금대기',
        value: deposit,
      },
      {
        title: '상품준비중',
        value: pending,
      },
      {
        title: '배송중',
        value: shipping,
      },
      {
        title: '배송완료',
        value: delivered,
      },
    ];
  }, [orderList]);

  /**
   * 위시리스트
   * --
   */
  const WishList = React.useMemo(() => {
    const newWish = wishlist
      .filter((wish) => wish.product_id && wish.type === 'WISH')
      .map((wish) => {
        // 위시리스트 상품 찾기
        const findProduct = products?.find(
          (product) => wish.product_id === product.product_id,
        );

        if (!findProduct) return null;

        const { product_file, price, product_id, product_name } = findProduct;

        // 썸네일 추출
        const thumbnail =
          product_file?.find((file) => file.type === 'THUMBNAIL') || null;

        // 적용 정책 추출
        const applyPolicy = policies?.filter((policy) =>
          policy.map_product_policy.some(
            (map) => map.product_id === product_id,
          ),
        );
        // 우선순위에 따른 적용되는 정책 추출
        const applicationPolicy = applyPolicy?.length
          ? extractApplicationPolicy(applyPolicy, price, promotion)
          : null;

        return {
          key: wish.wishlist_id,
          productKey: wish.product_id,
          src: thumbnail ? `${STORE_URL}/${thumbnail.file_url}` : null,
          title: product_name,
          price: price,
          discount: applicationPolicy ? applicationPolicy.discount_rate : null,
          type: applicationPolicy ? applicationPolicy.discount_type : null,
        };
      });

    return newWish;
  }, [wishlist, products, policies, promotion]);

  /**
   * 문의 목록
   * --
   */
  const InquiryList = React.useMemo(() => {
    const filterInquiry = inquiryList.filter(
      (inquiry) => inquiry.inquiry_type !== 'FAQ',
    );

    const newInquiryList = filterInquiry.reduce(
      (acc, inquiry) => {
        const { inquiry_type, inquiry_answer } = inquiry;

        const newInquiry = {
          ...inquiry,
          status: inquiry_answer ? 'DONE' : 'UNFINISHED',
        };

        if (inquiry_type === 'ONEINQUIRY') {
          acc['ONEINQUIRY'].push(newInquiry);
        } else {
          acc['PRODUCT'].push(newInquiry);
        }

        return acc;
      },
      { ONEINQUIRY: [], PRODUCT: [] },
    );

    return newInquiryList;
  }, [inquiryList]);

  /**
   * 등급 리스트 정제
   * --
   */
  const GradeList = React.useMemo(() => {
    if (!Grades?.length) {
      return {
        variable: [],
        fixed: [],
      };
    }

    const gradeList = Grades?.reduce(
      (acc, cur) => {
        const { is_fixed } = cur;

        if (is_fixed === 'Y') {
          acc['fixed'].push(cur);
        } else {
          acc['variable'].push(cur);
        }

        return acc;
      },
      {
        variable: [],
        fixed: [],
      },
    );

    return gradeList;
  }, [Grades]);
  // React.useEffect(() => {
  //   const handleTokenCheck = async () => {
  //     // 토큰 검증
  //     const cookie = getCookie('JM_COFFEE_ACCESS_TOKEN');
  //     if (cookie)
  //       try {
  //         const { status, data } = await authCheck();

  //         // NOTE: 검증 실패 처리
  //         if (status !== 200) {
  //           userAction.resetUser();
  //           userDataAction.resetAllData();
  //           deleteCookie('JM_COFFEE_ACCESS_TOKEN', {});
  //           return false;
  //         }

  //         // NOTE: 검증 성공
  //         userAction.setUser(data);
  //         return true;
  //       } catch (err) {
  //         console.error('[AUTH] ERROR!! ', err);
  //         return false;
  //       }
  //   };
  //   handleTokenCheck();
  // }, []);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageHomePresenter
        navigate={navigate}
        user={UserInfoData}
        recentOrderList={RecentOrderList}
        orderList={DeliveryInfo}
        wishlist={WishList}
        inquiries={InquiryList}
        screenSize={screenSize}
        grades={GradeList}
        totalPrice={Summary?.total_price}
        totalCount={Summary?.total_count}
        setting={settings?.grade_setting}
      />
    </>
  );
};

export default MypageHomeContainer;
